// Groups a flat selection of Tesla files (picked with a multi-select, so no folder paths) into events,
// and routes any selection to the right parser. Problems found along the way come back as PickNotes.

import type { VendorEvent } from './types'
import {
  buildTeslaJsonEvent,
  parseClipStartTime,
  readFileAsText,
  teslaExtractor,
  type TeslaEventJson
} from './tesla'
import { formatTime } from '@/utils/format'

export interface MissingCameraGap {
  clipTime: Date
  cameras: string[]
}

export type PickNote =
  | { kind: 'no-event-json'; count: number }
  | { kind: 'json-without-clips'; eventTime: Date }
  | { kind: 'missing-cameras'; eventId: string; eventTime: Date; gaps: MissingCameraGap[] }
  | { kind: 'unreadable-event-json'; count: number }
  | { kind: 'ignored-files'; count: number }

export interface TeslaSelection {
  events: VendorEvent[]
  notes: PickNote[]
}

// A clip joins an event if it starts up to 11 minutes before the event.json time or 1 minute after.
// The car saves the 10 minutes before the trigger; the extra minute covers the clip in progress.
const WINDOW_BEFORE_MS = 11 * 60_000
const WINDOW_AFTER_MS = 60_000

const CLIP_NAME = /^(\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2})-(.+)\.mp4$/i

interface Clip {
  file: File
  time: Date
  minuteKey: string
  camera: string
}

interface JsonEvent {
  file: File
  text: string
  meta: TeslaEventJson
  time: Date
  clips: Clip[]
}

function toClip(file: File): Clip | null {
  const m = file.name.match(CLIP_NAME)
  const time = parseClipStartTime(file.name)
  if (!m || !time || isNaN(time.getTime())) return null
  return { file, time, minuteKey: m[1], camera: m[2].toLowerCase() }
}

async function readEventJson(file: File): Promise<JsonEvent | null> {
  let meta: TeslaEventJson
  let text: string
  try {
    text = await readFileAsText(file)
    meta = JSON.parse(text)
  } catch {
    return null
  }
  const time = new Date(meta?.timestamp ?? NaN)
  if (isNaN(time.getTime())) return null
  return { file, text, meta, time, clips: [] }
}

function nearestEvent(clip: Clip, events: JsonEvent[]): JsonEvent | null {
  let best: JsonEvent | null = null
  let bestDistance = Infinity
  for (const event of events) {
    const offset = clip.time.getTime() - event.time.getTime()
    if (offset < -WINDOW_BEFORE_MS || offset > WINDOW_AFTER_MS) continue
    if (Math.abs(offset) >= bestDistance) continue
    best = event
    bestDistance = Math.abs(offset)
  }
  return best
}

function missingCameraGaps(clips: Clip[]): MissingCameraGap[] {
  const allCameras = [...new Set(clips.map((c) => c.camera))].sort()
  const byMinute = new Map<string, Clip[]>()
  for (const clip of clips)
    byMinute.set(clip.minuteKey, [...(byMinute.get(clip.minuteKey) ?? []), clip])

  const gaps: MissingCameraGap[] = []
  for (const minuteClips of byMinute.values()) {
    const present = new Set(minuteClips.map((c) => c.camera))
    const cameras = allCameras.filter((c) => !present.has(c))
    if (cameras.length > 0) gaps.push({ clipTime: minuteClips[0].time, cameras })
  }
  return gaps.sort((a, b) => a.clipTime.getTime() - b.clipTime.getTime())
}

function recentClipEvents(clips: Clip[]): VendorEvent[] {
  const byMinute = new Map<string, Clip[]>()
  for (const clip of clips)
    byMinute.set(clip.minuteKey, [...(byMinute.get(clip.minuteKey) ?? []), clip])

  return [...byMinute.entries()].map(([key, minuteClips]) => {
    const files = minuteClips.map((c) => c.file).sort((a, b) => a.name.localeCompare(b.name))
    return {
      id: `files:recent:${key}`,
      dateTime: minuteClips[0].time,
      location: undefined,
      displayLabel: 'Recent Clip',
      thumbnailFile: undefined,
      filesToUpload: files,
      rawMeta: { place: '', meta: {}, category: null }
    }
  })
}

// Overlapping event folders can hand over the same clip twice.
function dropDuplicates(files: File[]): File[] {
  const seen = new Set<string>()
  return files.filter((f) => {
    // event.json, thumb.png and event.mp4 share names across folders, so only clips are compared here.
    if (!CLIP_NAME.test(f.name)) return true
    const key = `${f.name}|${f.size}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Groups files picked from one or more event folders (no relative paths) into Tesla events. */
export async function groupFlatTeslaFiles(picked: File[]): Promise<TeslaSelection> {
  const files = dropDuplicates(picked)
  const jsonFiles = files.filter((f) => f.name === 'event.json')
  const thumbs = files.filter((f) => f.name === 'thumb.png')
  const eventVideos = files.filter((f) => f.name === 'event.mp4')
  const clips = files.map(toClip).filter((c): c is Clip => c !== null)

  const parsed = await Promise.all(jsonFiles.map(readEventJson))
  const readable = parsed.filter((e): e is JsonEvent => e !== null)
  const unreadable = parsed.length - readable.length
  // The same event.json picked twice (overlapping folders) is one event.
  const jsonEvents = readable.filter((e, i) => readable.findIndex((o) => o.text === e.text) === i)

  const unassigned: Clip[] = []
  for (const clip of clips) {
    const owner = nearestEvent(clip, jsonEvents)
    if (owner) owner.clips.push(clip)
    else unassigned.push(clip)
  }

  const single = jsonEvents.length === 1
  const attachedExtras = single ? thumbs.length + eventVideos.length : 0
  const ignored = files.length - jsonFiles.length - clips.length - attachedExtras

  const notes: PickNote[] = []
  const events: VendorEvent[] = []
  const usedIds = new Map<string, number>()

  for (const json of jsonEvents) {
    const extras = single ? [...thumbs, ...eventVideos] : []
    if (json.clips.length === 0 && extras.every((f) => f.name !== 'event.mp4')) {
      notes.push({ kind: 'json-without-clips', eventTime: json.time })
      continue
    }
    const baseId = `files:${json.meta.timestamp}`
    const seen = usedIds.get(baseId) ?? 0
    usedIds.set(baseId, seen + 1)
    const id = seen === 0 ? baseId : `${baseId}-${seen + 1}`

    events.push(
      buildTeslaJsonEvent(id, json.meta, [...json.clips.map((c) => c.file), ...extras], null)
    )
    const gaps = missingCameraGaps(json.clips)
    if (gaps.length > 0)
      notes.push({ kind: 'missing-cameras', eventId: id, eventTime: json.time, gaps })
  }

  events.push(...recentClipEvents(unassigned))
  if (unassigned.length > 0) notes.push({ kind: 'no-event-json', count: unassigned.length })
  if (unreadable > 0) notes.push({ kind: 'unreadable-event-json', count: unreadable })
  if (ignored > 0) notes.push({ kind: 'ignored-files', count: ignored })

  events.sort((a, b) => (b.dateTime?.getTime() ?? 0) - (a.dateTime?.getTime() ?? 0))
  return { events, notes }
}

function hasRelativePath(file: File): boolean {
  return !!(file as File & { webkitRelativePath?: string }).webkitRelativePath
}

/** The one entry point for a picked selection: folder parser when paths exist, flat grouping otherwise. */
export async function parseTeslaSelection(files: File[]): Promise<TeslaSelection> {
  if (files.some(hasRelativePath)) {
    if (!teslaExtractor.parseEvents) throw new Error('Tesla extractor cannot parse events')
    return { events: await teslaExtractor.parseEvents(files), notes: [] }
  }
  return groupFlatTeslaFiles(files)
}

function cameraLabel(camera: string): string {
  return camera.replace(/_/g, ' ')
}

function joinWords(words: string[]): string {
  if (words.length <= 1) return words.join('')
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

function describeMissingCameras(note: Extract<PickNote, { kind: 'missing-cameras' }>): string {
  const groups = new Map<string, { cameras: string[]; times: Date[] }>()
  for (const gap of note.gaps) {
    const key = gap.cameras.join('|')
    const group = groups.get(key) ?? { cameras: gap.cameras, times: [] }
    group.times.push(gap.clipTime)
    groups.set(key, group)
  }
  const parts = [...groups.values()].map(({ cameras, times }) => {
    const names = joinWords(cameras.map(cameraLabel))
    const noun = cameras.length === 1 ? 'camera' : 'cameras'
    return `no ${names} ${noun} for ${times.map((t) => formatTime(t)).join(', ')}`
  })
  return `${formatTime(note.eventTime)} event: ${parts.join('; ')}.`
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/** Plain-language text for a banner line. */
export function describePickNote(note: PickNote): string {
  switch (note.kind) {
    case 'missing-cameras':
      return describeMissingCameras(note)
    case 'ignored-files':
      return note.count === 1
        ? '1 file was not a TeslaCam clip and was skipped.'
        : `${note.count} files were not TeslaCam clips and were skipped.`
    case 'no-event-json':
      return `${note.count} ${plural(note.count, 'clip had', 'clips had')} no event.json, so ${plural(note.count, 'it is', 'they are')} shown as recent ${plural(note.count, 'clip', 'clips')}.`
    case 'json-without-clips':
      return `The ${formatTime(note.eventTime)} event had no video clips with it, so it was skipped.`
    case 'unreadable-event-json':
      return `${note.count} event.json ${plural(note.count, 'file', 'files')} could not be read, so ${plural(note.count, 'its clips are', 'their clips are')} shown as recent clips.`
  }
}

/** Banner heading: "missing" only when clips or cameras are really missing, else the notes are just skips. */
export function pickNotesHeading(notes: PickNote[]): string {
  const missing = notes.some((n) => n.kind === 'missing-cameras' || n.kind === 'json-without-clips')
  return missing ? 'A few files are missing' : 'Some files were skipped'
}
